import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Mail, MapPin, Clock, ShieldCheck, CheckCircle2, ArrowRight, Lock, MessageSquare,
} from 'lucide-react';
import {
  EMAIL, BRAND, BUSINESS_HOURS,
} from '../lib/siteConfig';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'family_patient', label: 'Family / Patient' },
  { value: 'clinician', label: 'Clinician (RN, LPN, aide, therapist)' },
  { value: 'agency_facility', label: 'Agency / Facility / Organization' },
  { value: 'other', label: 'Other' },
];

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    city: '', state: '', role: 'family_patient', message: '',
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const roleLabel = ROLES.find((r) => r.value === form.role)?.label || form.role;
      const res = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${form.first_name} ${form.last_name}`.trim(),
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          city: form.city,
          state: form.state,
          role: roleLabel,
          message: form.message,
          form_type: 'general',
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Message received — we\u2019ll be in touch shortly.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const schema = [
    organizationSchema(),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Contact', path: '/contact' },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="contact-page">
      <Seo
        title="Contact CaraLoom | Get in Touch"
        description="Questions about CaraLoom's home-care marketplace? Get in touch with our team. CaraLoom connects families with verified, independent clinicians — we'll help you get started."
        path="/contact"
        keywords={['contact caraloom', 'home care marketplace support', 'caraloom help']}
        schema={schema}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="cn-container pt-12 pb-8">
        <div className="cn-eyebrow mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-cn-accent" /> Contact us
        </div>
        <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-4 max-w-3xl">
          Questions? We&rsquo;re <span className="italic text-cn-primary">here to help.</span>
        </h1>
        <p className="text-lg text-cn-text-muted max-w-2xl">
          {BRAND} is a marketplace that connects you with verified, independent clinicians &mdash; we
          don&rsquo;t provide care directly. Have a question or need a hand getting started? Send us a
          note and our team will get back to you within one business day.
        </p>
      </section>

      {/* Main */}
      <section className="cn-container pb-20">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: contact info + map */}
          <div className="lg:col-span-5 space-y-6">
            <div className="cn-card p-7 rounded-3xl space-y-5">
              <a href={`mailto:${EMAIL}`} className="flex items-start gap-4 group" data-testid="contact-email-block">
                <div className="w-11 h-11 rounded-xl bg-cn-soft flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-cn-primary" />
                </div>
                <div>
                  <div className="cn-eyebrow">Email</div>
                  <div className="text-base font-medium text-cn-text group-hover:text-cn-primary transition-colors">{EMAIL}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-cn-soft flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-cn-primary" />
                </div>
                <div>
                  <div className="cn-eyebrow">Service area</div>
                  <div className="text-base text-cn-text">United States &mdash; verified clinicians in a growing number of cities</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-cn-soft flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-cn-primary" />
                </div>
                <div>
                  <div className="cn-eyebrow">Hours</div>
                  {BUSINESS_HOURS.map((h) => (
                    <div key={h.days} className="text-sm text-cn-text-muted">{h.days}: <span className="text-cn-text">{h.hours}</span></div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden cn-card">
              <iframe
                title="CaraLoom service area map"
                src="https://maps.google.com/maps?q=United%20States&z=3&output=embed"
                className="w-full h-64 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>

            <div className="rounded-2xl bg-cn-soft/50 border border-cn-border p-5 flex items-start gap-3" data-testid="contact-hipaa-notice">
              <Lock className="w-5 h-5 text-cn-primary mt-0.5 flex-shrink-0" />
              <p className="text-sm text-cn-text-muted leading-relaxed">
                <span className="font-medium text-cn-text">Your privacy matters.</span> Please don&rsquo;t share
                sensitive medical details (PHI) in this form. This channel is not for medical emergencies — if
                this is an emergency, call <span className="font-semibold text-cn-error">911</span>.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="cn-card p-10 rounded-3xl text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-cn-soft flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-cn-primary" />
                </div>
                <h2 className="cn-display text-3xl text-cn-text mb-3">Message received</h2>
                <p className="text-cn-text-muted max-w-md mx-auto mb-7">
                  Thanks for reaching out. Our team will get back to you within one business day.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Link to="/" className="cn-btn cn-btn-ghost">Back to home</Link>
                  <Link to="/patient/find-clinicians" className="cn-btn cn-btn-primary">Find a clinician <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="cn-card p-7 md:p-9 rounded-3xl" data-testid="contact-form">
                <h2 className="cn-display text-2xl text-cn-text mb-1">Send us a message</h2>
                <p className="text-sm text-cn-text-muted mb-6">We respond within one business day.</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First name *">
                    <Input value={form.first_name} onChange={set('first_name')} placeholder="Jane" required data-testid="contact-first-name" />
                  </Field>
                  <Field label="Last name *">
                    <Input value={form.last_name} onChange={set('last_name')} placeholder="Doe" required data-testid="contact-last-name" />
                  </Field>
                  <Field label="Email *">
                    <Input type="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" required data-testid="contact-email" />
                  </Field>
                  <Field label="Phone">
                    <Input type="tel" value={form.phone} onChange={set('phone')} placeholder="(202) 555-0123" data-testid="contact-phone" />
                  </Field>
                  <Field label="City">
                    <Input value={form.city} onChange={set('city')} placeholder="Your city" data-testid="contact-city" />
                  </Field>
                  <Field label="State">
                    <Input value={form.state} onChange={set('state')} placeholder="NY" maxLength={20} data-testid="contact-state" />
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="I am a... *">
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="contact-role"><SelectValue placeholder="Select your role" /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r.value} value={r.value} data-testid={`contact-role-${r.value}`}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="How can we help? *">
                    <Textarea value={form.message} onChange={set('message')} rows={4} required
                      placeholder="Tell us what you'd like help with. (Please avoid sensitive medical details.)"
                      data-testid="contact-message" />
                  </Field>
                </div>

                <Button type="submit" disabled={loading} className="w-full h-12 mt-6 cn-btn cn-btn-primary text-base" data-testid="contact-submit">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Send message <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>

                <p className="text-xs text-cn-text-muted text-center mt-4 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-cn-primary" />
                  By submitting, you agree to our <Link to="/privacy" className="text-cn-primary hover:underline">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-cn-text mb-1.5">{label}</label>
    {children}
  </div>
);
